import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Users, Star, Building2, Target, Activity } from 'lucide-react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { cn } from '../../../../../lib/utils';
import apiService from '../../../../../services/api.service';

const ICON_MAP = {
  'Total Users': { icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  'Total Reviews': { icon: Star, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  'Total Departments': { icon: Building2, color: 'text-green-500', bg: 'bg-green-500/10' },
  'Total Goals': { icon: Target, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  'Total Activities': { icon: Activity, color: 'text-orange-500', bg: 'bg-orange-500/10' },
};

function StatsCards({ startDate, endDate }) {
  const [cardsData, setCardsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCardsData = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = `startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
      const response = await apiService.admin.getAdminTotalCount(query);
      setCardsData([
        { title: 'Total Users', count: response.users || 0 },
        { title: 'Total Reviews', count: response.reviews || 0 },
        { title: 'Total Departments', count: response.departments || 0 },
        { title: 'Total Goals', count: response.goals || 0 },
        { title: 'Total Activities', count: response.activities || 0 },
      ]);
    } catch (error) {
      console.error('Failed to fetch card data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchCardsData();
  }, [fetchCardsData]);

  if (isLoading || !cardsData.length) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {Array.from({ length: 5 }, (_, index) => (
          <Card key={index} style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-4">
              <div className="animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-muted" />
                  <div className="flex-1">
                    <div className="h-3 w-20 bg-muted rounded mb-2" />
                    <div className="h-6 w-12 bg-muted rounded" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {cardsData.map(({ title, count }) => {
        const iconConfig = ICON_MAP[title] || {
          icon: Activity,
          color: 'text-gray-500',
          bg: 'bg-gray-500/10',
        };
        const Icon = iconConfig.icon;

        return (
          <Card key={title} style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={cn('p-2.5 rounded-lg', iconConfig.bg)}>
                  <Icon className={cn('h-5 w-5', iconConfig.color)} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{title}</p>
                  <p className="text-2xl font-bold">{count.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

StatsCards.propTypes = {
  startDate: PropTypes.instanceOf(dayjs).isRequired,
  endDate: PropTypes.instanceOf(dayjs).isRequired,
};

export default StatsCards;
